import { useCallback, useRef } from "react";
import httpClient, { AuthNotReadyError } from "../utils/httpClient";
import { JobProps } from "../types";
import { idbSet } from "../utils/idbCache";
import { sleep } from "../utils/sleep";

const MAX_RETRIES = 3;
const RETRY_DELAY_MS = 2000;

export const useLoadJobs = (
  setAllJobs: (data: JobProps[]) => void,
  showError: (message: string) => void
) => {
  const inFlightRef = useRef(false);

  const loadJobs = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;

    const fetchOnce = async (): Promise<JobProps[] | null> => {
      try {
        const res = await httpClient.get<JobProps[]>("/api/cba/jobs");
        return Array.isArray(res.data) ? res.data : null;
      } catch (e) {
        if (e instanceof AuthNotReadyError || (e as any)?.response?.status === 401) {
          return null; // Silent retry
        }
        throw e; // Real error
      }
    };

    try {
      let data = await fetchOnce();

      let attempts = 0;
      while (data === null && attempts < MAX_RETRIES) {
        await sleep(RETRY_DELAY_MS);
        data = await fetchOnce();
        attempts++;
      }

      if (data === null) {
        showError("An error occurred while loading jobs. Please try again later.");
        return;
      }

      setAllJobs(data);

      try {
        await idbSet("cba.allJobs", data);
      } catch (e) {
        console.warn("Caching jobs to IndexedDB failed.", e);
      }
    } catch (e: any) {
      console.error("Error fetching jobs:", e.message, e.response?.status);
      showError("An error occurred while loading jobs. Please try again later.");
    } finally {
      inFlightRef.current = false;
    }
  }, [setAllJobs, showError]);

  return { loadJobs };
};